import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, ExternalLink, FileText, RefreshCw, Send } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const fmtGBP = (pence) => `£${((pence || 0) / 100).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDT = (iso) => {
    if (!iso) return "—";
    return new Date(iso).toLocaleString("en-GB", {
        day: "2-digit", month: "short", year: "numeric",
        hour: "2-digit", minute: "2-digit",
    });
};

const statusPill = (s) => {
    if (s === "paid") return { cls: "bg-emerald-100 text-emerald-800 border-emerald-300", label: "PAID" };
    if (s === "email_failed") return { cls: "bg-red-100 text-red-800 border-red-300", label: "EMAIL FAILED" };
    return { cls: "bg-neutral-100 text-neutral-700 border-neutral-300", label: "SENT" };
};

const Invoices = () => {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [busy, setBusy] = useState(null);

    const load = () => {
        setLoading(true);
        api.get("/invoices?limit=200")
            .then((r) => setItems(r.data.items || []))
            .catch(() => toast.error("Failed to load invoices"))
            .finally(() => setLoading(false));
    };
    useEffect(load, []);

    const markPaid = async (id) => {
        setBusy(id);
        try {
            await api.post(`/invoices/${id}/mark-paid`);
            toast.success("Marked paid");
            load();
        } catch {
            toast.error("Failed");
        } finally {
            setBusy(null);
        }
    };
    const resend = async (id) => {
        setBusy(id);
        try {
            await api.post(`/invoices/${id}/resend`);
            toast.success("Invoice resent");
        } catch {
            toast.error("Resend failed");
        } finally {
            setBusy(null);
        }
    };

    const openInvoice = async (id) => {
        try {
            const r = await api.get(`/invoices/${id}`);
            if (r.data.view_url) window.open(r.data.view_url, "_blank");
        } catch {
            toast.error("Could not open invoice");
        }
    };

    return (
        <div className="space-y-6" data-testid="invoices-page">
            <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                    <p className="text-label" style={{ color: "var(--dh-muted)" }}>Billing</p>
                    <h2 className="font-display font-black text-3xl sm:text-4xl uppercase tracking-tight leading-tight">
                        Invoices
                    </h2>
                    <p className="text-sm text-neutral-600 mt-2 max-w-xl">
                        Every invoice raised from a completed job. Click a row to open
                        the hosted invoice page; use the buttons to resend the email or
                        mark it as paid.
                    </p>
                </div>
                <div className="flex gap-2">
                    <Link
                        to="/settings"
                        className="dh-btn"
                        data-variant="ghost"
                        data-testid="invoice-settings-link"
                    >
                        Invoice settings
                    </Link>
                    <button
                        onClick={load}
                        className="dh-btn"
                        data-variant="ghost"
                        data-testid="invoices-refresh"
                    >
                        <RefreshCw size={16} /> Refresh
                    </button>
                </div>
            </div>

            {loading ? (
                <p className="text-neutral-500">Loading…</p>
            ) : items.length === 0 ? (
                <div
                    className="border-2 border-dashed border-neutral-300 p-12 text-center"
                    data-testid="invoices-empty"
                >
                    <FileText size={32} className="mx-auto text-neutral-400 mb-3" />
                    <p className="text-neutral-600 font-medium">
                        No invoices yet. Complete a job to raise one.
                    </p>
                </div>
            ) : (
                <div
                    className="border-2 border-[color:var(--dh-line)] bg-white overflow-hidden"
                    data-testid="invoices-table"
                >
                    <div className="hidden md:grid grid-cols-12 gap-4 px-4 py-3 bg-[color:var(--dh-ink)] text-white text-xs font-bold uppercase tracking-wide">
                        <div className="col-span-2">Number</div>
                        <div className="col-span-3">Customer</div>
                        <div className="col-span-3">Issued</div>
                        <div className="col-span-2">Amount</div>
                        <div className="col-span-2 text-right">Actions</div>
                    </div>
                    <ul>
                        {items.map((inv, idx) => {
                            const p = statusPill(inv.status);
                            return (
                                <li
                                    key={inv.id}
                                    className="border-t border-neutral-200 first:border-t-0 px-4 py-3 md:grid md:grid-cols-12 md:gap-4 md:items-center"
                                    data-testid={`invoice-row-${idx}`}
                                >
                                    <div className="md:col-span-2 min-w-0">
                                        <button
                                            className="font-mono font-bold text-sm underline"
                                            data-testid={`invoice-open-${inv.id}`}
                                            onClick={() => openInvoice(inv.id)}
                                        >
                                            {inv.number}
                                        </button>
                                        <p className="mt-1">
                                            <span
                                                className={`inline-block text-[10px] font-bold px-2 py-0.5 border ${p.cls}`}
                                                data-testid={`invoice-status-${inv.id}`}
                                            >
                                                {p.label}
                                            </span>
                                        </p>
                                    </div>
                                    <div className="md:col-span-3 min-w-0 mt-2 md:mt-0">
                                        <p className="font-bold text-sm truncate" data-testid={`invoice-customer-${inv.id}`}>
                                            {inv.customer_name || "—"}
                                        </p>
                                        <p className="text-xs text-neutral-500 truncate">
                                            {inv.address}
                                        </p>
                                    </div>
                                    <div className="md:col-span-3 text-xs text-neutral-700 font-mono mt-2 md:mt-0">
                                        {fmtDT(inv.issued_at)}
                                    </div>
                                    <div className="md:col-span-2 text-lg font-bold mt-2 md:mt-0" data-testid={`invoice-amount-${inv.id}`}>
                                        {fmtGBP(inv.total_pence)}
                                    </div>
                                    <div className="md:col-span-2 flex md:justify-end gap-1 mt-2 md:mt-0 flex-wrap">
                                        <button
                                            className="dh-btn"
                                            data-variant="ghost"
                                            data-testid={`invoice-view-${inv.id}`}
                                            onClick={() => openInvoice(inv.id)}
                                            title="Open hosted invoice"
                                        >
                                            <ExternalLink size={14} />
                                        </button>
                                        <button
                                            className="dh-btn"
                                            data-variant="ghost"
                                            data-testid={`invoice-resend-${inv.id}`}
                                            onClick={() => resend(inv.id)}
                                            disabled={busy === inv.id}
                                            title="Resend email"
                                        >
                                            <Send size={14} />
                                        </button>
                                        {inv.status !== "paid" && (
                                            <button
                                                className="dh-btn"
                                                data-testid={`invoice-mark-paid-${inv.id}`}
                                                onClick={() => markPaid(inv.id)}
                                                disabled={busy === inv.id}
                                                title="Mark paid"
                                            >
                                                <CheckCircle2 size={14} /> Paid
                                            </button>
                                        )}
                                    </div>
                                </li>
                            );
                        })}
                    </ul>
                </div>
            )}
        </div>
    );
};

export default Invoices;
